import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { api, ApiError } from '@/lib/api';
import { Alert, AlertDescription } from '@/components/shadcn/alert';
import { Button } from '@/components/shadcn/button';
import { Checkbox } from '@/components/shadcn/checkbox';
import { Input } from '@/components/shadcn/input';
import { Label } from '@/components/shadcn/label';
import { Logo } from '@/components/ui/Logo';
import { GoogleMark } from '@/components/ui/GoogleMark';
import { AuthBrand, AuthLayout } from './AuthLayout';

/**
 * "Continue with Google", shown only where it will work.
 *
 * Whether Google is configured is a server fact. Rendering the button on a
 * deployment without credentials would send the customer away and fail at the
 * token exchange, which is a worse outcome than not offering it.
 */
function GoogleButton() {
  const { lang } = useI18n();
  const [health, setHealth] = useState<{ features?: { google?: boolean } } | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetch('/api/health')
      .then((r) => r.json())
      .then((d) => {
        if (!cancelled) setHealth(d);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  if (health?.features?.google !== true) return null;

  return (
    <div className="mt-8">
      <Button
        type="button"
        variant="outline"
        size="lg"
        className="h-11 w-full"
        onClick={() => {
          window.location.href = '/api/erp/auth/google?next=/app/dashboard';
        }}
      >
        <GoogleMark className="size-4" />
        {lang === 'ar' ? 'المتابعة بحساب Google' : 'Continue with Google'}
      </Button>
      <div className="mt-6 flex items-center gap-3">
        <span className="h-px flex-1 bg-border" />
        <span className="text-xs text-muted-foreground">{lang === 'ar' ? 'أو' : 'or'}</span>
        <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  );
}

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  remember: z.boolean().optional(),
});

type LoginValues = z.infer<typeof schema>;

function LoginAside() {
  const { t, lang } = useI18n();
  /* Public endpoint — the same one railit.ai reads, so the marketing page and
     the sign-in pane cannot quote different prices. */
  const { data: offers } = useQuery<{ cheapest_usd: number; trial_days: number }>({
    queryKey: ['public-offers'],
    queryFn: async () => (await api<{ offers: { cheapest_usd: number; trial_days: number } }>('/billing/plans')).offers,
    staleTime: 30 * 60_000,
  });
  return (
    <>
      <AuthBrand />

      <div className="relative max-w-xl">
        <h1 className="text-5xl leading-[1.05] font-medium tracking-tight text-balance xl:text-6xl">
          {lang === 'ar' ? (
            <>
              كل الشركة،
              <br />
              في <span className="whitespace-nowrap text-brand-ink">مكان واحد.</span>
            </>
          ) : (
            <>
              The whole company,
              <br />
              in{' '}
              <span className="relative whitespace-nowrap text-brand-ink">
                one place.
                <span
                  aria-hidden
                  className="absolute inset-x-0 bottom-1 -z-10 h-2.5 rounded-full bg-primary/25"
                />
              </span>
            </>
          )}
        </h1>
        <p className="mt-6 max-w-md text-lg leading-relaxed text-muted-foreground">
          {lang === 'ar' ? (
            <>
              المحاسبة، العملاء، الموظفون، المخزون، نقاط البيع والذكاء الاصطناعي —{' '}
              <b className="font-medium text-foreground">نظام واحد لشركتك</b>.
            </>
          ) : (
            <>
              Finance, CRM, HR, inventory, POS and AI agents —{' '}
              <b className="font-medium text-foreground">one system for your company</b>.
            </>
          )}
        </p>
      </div>

      {/*
        The price and the trial length come from the SERVER, not from copy.

        These two lines used to be translated strings reading "BHD 8 / month"
        and "14 days free". Neither has been true for some time — no plan costs
        BHD 8 and the trial is ten days — so the first thing a prospective
        customer read was a price this product does not charge. A number that
        can go stale must not live in a language file.
      */}
      <dl className="relative flex flex-wrap gap-x-12 gap-y-4 text-sm">
        <div>
          <dt className="text-muted-foreground">{t('from_price')}</dt>
          <dd className="mt-1 font-medium tabular-nums">
            {offers ? `$${offers.cheapest_usd} / ${t('per_month')}` : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">{t('trial')}</dt>
          <dd className="mt-1 font-medium tabular-nums">
            {offers ? t('trial_days_n').replace('{n}', String(offers.trial_days)) : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">railit.ai</dt>
          <dd className="mt-1 flex items-center gap-1.5 font-medium text-success">
            <span className="size-1.5 rounded-full bg-current" aria-hidden />
            {t('no_card_needed')}
          </dd>
        </div>
      </dl>

      {/* Ambient mark, bled off the trailing edge. */}
      <Logo
        size={360}
        markOnly
        aria-hidden
        className="pointer-events-none absolute -bottom-32 -end-32 opacity-[0.04]"
      />
    </>
  );
}

export function Login() {
  const { t, lang } = useI18n();
  const { login, isAuthenticated } = useAuth();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '', remember: true },
  });

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await login(values.email, values.password, values.remember ?? true);
    } catch (err) {
      if (err instanceof ApiError) {
        // No password on this identity — it was created with Google. Say which
        // door to use instead of "invalid credentials", which would send them
        // to reset a password they never set.
        if (err.code === 'USE_GOOGLE') {
          setSubmitError(
            lang === 'ar'
              ? 'هذا الحساب يسجّل الدخول عبر Google. استخدم زر Google بالأعلى.'
              : 'This account signs in with Google — use the button above.',
          );
        } else if (err.status === 401 || err.status === 403) {
          setSubmitError(t('invalid_credentials'));
        } else {
          setSubmitError(err.message || t('invalid_credentials'));
        }
      } else {
        setSubmitError(t('invalid_credentials'));
      }
    }
  });

  return (
    <AuthLayout aside={<LoginAside />}>
      <div className="mb-8 lg:hidden">
        <Logo size={24} />
      </div>

      <div className="space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">{t('sign_in')}</h2>
        <p className="text-sm text-muted-foreground">{t('sign_in_lede')}</p>
      </div>

      {/* The same door as sign-up. An account created with Google has no
          password at all, so without this there is no way back in. */}
      <GoogleButton />

      <form onSubmit={onSubmit} autoComplete="off" noValidate className="mt-8 space-y-5">
        {submitError ? (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{submitError}</AlertDescription>
          </Alert>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="login-email">{t('email')}</Label>
          <Input
            id="login-email"
            type="email"
            placeholder="you@company.com"
            autoComplete="username"
            aria-invalid={errors.email ? true : undefined}
            {...register('email')}
          />
          {errors.email ? (
            <p className="text-xs text-destructive">{t('email')}</p>
          ) : null}
        </div>

        <div className="space-y-2">
          <Label htmlFor="login-password">{t('password')}</Label>
          <Input
            id="login-password"
            type="password"
            placeholder="••••••••"
            autoComplete="current-password"
            aria-invalid={errors.password ? true : undefined}
            {...register('password')}
          />
          {errors.password ? (
            <p className="text-xs text-destructive">{t('password')}</p>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <Checkbox
            id="login-remember"
            checked={watch('remember') ?? true}
            onCheckedChange={(v: boolean | 'indeterminate') =>
              setValue('remember', v === true)
            }
          />
          <Label htmlFor="login-remember" className="font-normal text-muted-foreground">
            {t('keep_signed_in')}
          </Label>
        </div>

        <Button type="submit" className="h-10 w-full" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : null}
          {t('continue_')}
          {!isSubmitting ? <ArrowRight className="size-4 rtl:rotate-180" /> : null}
        </Button>
      </form>

      <p className="mt-8 text-center text-sm text-muted-foreground">
        {t('no_account')}{' '}
        <Link to="/signup" className="font-medium text-brand-ink hover:underline">
          {t('start_free_trial')}
        </Link>
      </p>
    </AuthLayout>
  );
}
